import React, { Suspense, useEffect, useState } from 'react';
import {
  commitLocalUpdate,
  useLazyLoadQuery,
  useRelayEnvironment,
} from 'react-relay/hooks';
import { useLocation } from 'react-router-dom';
import { useFeatureFlag } from '@sparknz/feature-flags';
import {
  ContentFragmentInfoLayer,
  ErrorBoundary,
  useGlobalUser,
  useLoginModal,
  useSegment,
} from '@sparknz/journeys-common';
import { ActionLayer, Box, Button } from '@sparknz/spark-pattern-library';
import { trackCustomLink } from '@sparknz/tracking';

import { MobilePlanCardsProductsQuery } from '__generated__/MobilePlanCardsProductsQuery.graphql';
import { PlanCardLoaders } from 'components/shop/shared/components';
import { ConfigurablePlanCards } from 'components/shop/shared/typings/plans';
import { NETWORK_COVERAGE_PAGE } from 'shared/constants/path.constants';
import { INFO_LAYER_CONTENT_QUERY_KEY } from 'shared/constants/query.constants';
import useQuery from 'shared/hooks/useQuery';
import { QueryParameterKey } from '../../constants/query.constants';
import {
  ChangePlanProvider,
  PlansRefetchProvider,
  SelectedPlansProvider,
  useChangePlanDetails,
  useMobilePlansContent,
  usePlansRefetch,
  useSelectedPlans,
} from '../../providers';
import { ChangePlanActionTypes } from '../../reducer/changePlan.reducer';
import { hasMarketingTag } from '../../utils/getPromoDetails';
import { doPlansContainMultipleCtas } from '../../utils/mutliCtas.utils';
import BottomBar from '../BottomBar';
import GenericErrorFallback from '../GenericErrorFallback';
import MobilePlanCardActionsSection from '../MobilePlanCardActionsSection';
import PlanCard from '../PlanCard';
import SelectedPlansActionLayer from '../SelectedPlansActionLayer';
import { CategoryPromotionBanner } from './components/CategoryPromotionBanner';
import ChangePlanAccountSelectionContent from './components/ChangePlanAccountSelectionContent';
import ChangePlanLineSelectionContent from './components/ChangePlanLineSelectionContent';
import PlanCardsContainer from './components/PlanCardsContainer';
import PromotionMessageContainer from './components/PromotionMessageContainer';
import { mobilePlanCardsProductsQuery } from './queries/MobilePlanCardsProductsQuery';
import { ViewState } from './types';

interface MobilePlanCardsProps extends ConfigurablePlanCards {
  selectedCategoryId: string;
  selectedSubCategoryId: string;
  isPersistedQueryEnabled?: boolean;
}

const components: Record<ViewState, React.ComponentType> = {
  [ViewState.ManageAccount]: ChangePlanAccountSelectionContent,
  [ViewState.ManageLines]: ChangePlanLineSelectionContent,
};

const getPlanNameById = (
  planId: string,
  planCardsProducts: MobilePlanCardsProductsQuery['response']['eligibleProductOffersByCategory']['category']['plans'],
) => {
  return (
    planCardsProducts.find((plans) => plans.planDetails.planId === planId)
      ?.planDetails?.planName || null
  );
};

function MobilePlanCardsInternal({
  selectedCategoryId,
  selectedSubCategoryId,
  isPersistedQueryEnabled,
  variant = 'standard',
  forceCarousel = true,
}: MobilePlanCardsProps) {
  const [isInfoLayerOpen, setIsInfoLayerOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [infoLayerContentKey, setInfoContentLayerKey] = useState<string>('');
  const { fetchKey, setFetchKey } = usePlansRefetch();
  const { query, replaceQuery } = useQuery();
  const { promotionBanner } = useMobilePlansContent();
  const { openLoginModal } = useLoginModal();
  const { showActionLayer, viewState, dispatch } = useChangePlanDetails();
  const {
    isSelectedPlansLayerOpen,
    openSelectedPlansLayer,
    closeSelectedPlansLayer,
    setSelectedPlanByQueryParameters,
  } = useSelectedPlans();

  const environment = useRelayEnvironment();

  const {
    authenticatedInfo: { isLoggedIn, hasSparkId },
    refetchUser,
  } = useGlobalUser();

  const location = useLocation();
  const queryParameters = new URLSearchParams(location.search);
  const planIdQueryParameter = queryParameters.get(
    QueryParameterKey.SelectedPlanId,
  );
  const planQuantityQueryParameter = queryParameters.get(
    QueryParameterKey.SelectedPlanQuantity,
  );

  const { channel, isBusiness, isPersonal } = useSegment();

  const getChangePlanActionLayerProp = () => {
    switch (viewState) {
      case ViewState.ManageAccount:
        return { title: 'Account selection', subTitle: 'Account' };

      case ViewState.ManageLines:
        return { title: 'Mobile phone plans', subTitle: 'Line' };

      default:
        return { title: '', subTitle: '' };
    }
  };

  const closeActionLayer = () => {
    dispatch({
      type: ChangePlanActionTypes.CloseActionLayer,
    });
  };

  const productDetailsResponse = useLazyLoadQuery<MobilePlanCardsProductsQuery>(
    mobilePlanCardsProductsQuery,
    {
      categoryId: selectedSubCategoryId,
      channel,
      ...(variant === 'refreshed' ? { includeCompanions: true } : {}),
    },
    { fetchKey },
  );

  const { plans: planCardsProducts, promotions } =
    productDetailsResponse.eligibleProductOffersByCategory.category;

  useEffect(() => {
    if (
      planIdQueryParameter &&
      planQuantityQueryParameter &&
      !isNaN(Number(planQuantityQueryParameter))
    ) {
      const selectedPlanName = getPlanNameById(
        planIdQueryParameter,
        planCardsProducts,
      );
      if (selectedPlanName) {
        setSelectedPlanByQueryParameters(
          planIdQueryParameter,
          selectedPlanName,
          Number(planQuantityQueryParameter),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planIdQueryParameter, planQuantityQueryParameter, planCardsProducts]);

  function handleInfoLayerChange(contentKey: string) {
    if (isPersistedQueryEnabled) {
      openInfoLayer(contentKey);
      return;
    }

    setInfoContentLayerKey(contentKey);
    setIsInfoLayerOpen(true);
  }

  function openInfoLayer(contentKey: string) {
    replaceQuery({
      ...query,
      [INFO_LAYER_CONTENT_QUERY_KEY]: contentKey,
    });
  }

  function handleSignInClick() {
    trackCustomLink('Sign in');
    openLoginModal({ onLoginSuccess: handleLoginSuccess });
  }

  function handleViewCoverageMapClick() {
    window.location.assign(NETWORK_COVERAGE_PAGE);
  }

  function handleLoginSuccess() {
    // Invalidate cached queries to ensure data is refetched
    // in an authenticated state when requested
    commitLocalUpdate(environment, (store) => {
      store.invalidateStore();
    });

    refetchUser();
    refetchPlans();
  }

  function refetchPlans() {
    setFetchKey((prev) => prev + 1);
  }

  const hasMultiCtas = doPlansContainMultipleCtas(planCardsProducts);

  const activeCategories = {
    category: selectedCategoryId,
    subCategory: selectedSubCategoryId,
  };

  const isPostpaidSelected = selectedCategoryId === 'postpaid';
  const isChangePlanSelected = selectedCategoryId === 'change_plan';

  const ActionLayerChild = components[viewState];

  const actionLayerPropsData = getChangePlanActionLayerProp();

  const promotion = promotions?.find(
    ({ productType }) => productType === 'DEAL',
  );

  const isPromotionBannerVisible =
    promotion || (promotionBanner?.title && isPostpaidSelected);

  const isStandardPlanCards = variant === 'standard';
  const isAuthenticatedSparkUser = isLoggedIn && hasSparkId;
  const isSignInPromptVisible =
    !isStandardPlanCards && !isAuthenticatedSparkUser && isPersonal;

  return (
    <>
      <ContentFragmentInfoLayer
        isOpen={isInfoLayerOpen}
        onClose={() => {
          setIsInfoLayerOpen(false);
        }}
        contentFragmentKey={infoLayerContentKey}
        segment={isBusiness ? 'business' : 'consumer'}
      />

      {isPromotionBannerVisible ? (
        <CategoryPromotionBanner
          promotion={promotion}
          promotionBanner={promotionBanner}
        />
      ) : null}

      {isSignInPromptVisible ? (
        <PromotionMessageContainer>
          To see eligible discounts, please{' '}
          <Button variant="textbutton" onClick={handleSignInClick}>
            Sign in
          </Button>
        </PromotionMessageContainer>
      ) : null}

      <Box pt={isSignInPromptVisible ? 's0_5' : 's3'}>
        Explore our award-winning mobile network coverage and 30-Day Mobile
        Network Guarantee.&nbsp;
        <Button variant="textbutton" onClick={handleViewCoverageMapClick}>
          View coverage map and learn more
        </Button>
      </Box>

      {planCardsProducts ? (
        <PlanCardsContainer
          numberOfCards={planCardsProducts.length}
          activeCategories={activeCategories}
          regionId="mobilePlans.carousel"
          forceCarousel={forceCarousel}
        >
          {planCardsProducts.map((planCardsProduct) => (
            <PlanCard
              key={planCardsProduct.planDetails.planId}
              variant={variant}
              planCardProduct={planCardsProduct}
              isAccordionOpen={isExpanded}
              setIsAccordionOpen={setIsExpanded}
              handleInfoLayerChange={handleInfoLayerChange}
              hasMarketingTag={hasMarketingTag(planCardsProducts)}
              activeCategories={activeCategories}
              renderCtaSection={({ planCardProduct }) => (
                <MobilePlanCardActionsSection
                  ctaOverrides={planCardProduct.ctaOverrides}
                  hasMultiCtas={hasMultiCtas}
                  planCardProduct={planCardProduct}
                  isMultiPlanEnabled={isStandardPlanCards}
                />
              )}
            />
          ))}
        </PlanCardsContainer>
      ) : null}
      {!isChangePlanSelected && isStandardPlanCards ? (
        <BottomBar
          planCardsProducts={planCardsProducts}
          openSelectedPlansLayer={openSelectedPlansLayer}
        />
      ) : null}
      <ActionLayer
        isOpen={showActionLayer}
        onClose={closeActionLayer}
        title={actionLayerPropsData.title}
        subtitle={actionLayerPropsData.subTitle}
        status="default"
      >
        <ActionLayerChild />
      </ActionLayer>

      <SelectedPlansActionLayer
        isOpen={isSelectedPlansLayerOpen}
        onClose={closeSelectedPlansLayer}
        planCardsProducts={planCardsProducts}
        isMultiPlanEnabled={isStandardPlanCards}
      />
    </>
  );
}

function ErrorFallback() {
  return (
    <Box pt="s4">
      <GenericErrorFallback />
    </Box>
  );
}

const MobilePlanCards = (props: MobilePlanCardsProps) => {
  const { value: isPersistedQueryEnabled, isLoading } = useFeatureFlag(
    'release-mobile-plan-cards-persisted-query',
  );

  if (isLoading) {
    return <PlanCardLoaders />;
  }

  return (
    <ErrorBoundary fallback={ErrorFallback}>
      <Suspense fallback={<PlanCardLoaders />}>
        <SelectedPlansProvider>
          <ChangePlanProvider>
            <PlansRefetchProvider>
              <MobilePlanCardsInternal
                {...props}
                isPersistedQueryEnabled={isPersistedQueryEnabled}
              />
            </PlansRefetchProvider>
          </ChangePlanProvider>
        </SelectedPlansProvider>
      </Suspense>
    </ErrorBoundary>
  );
};

export default MobilePlanCards;
